import React from 'react';
import { cn } from '../../lib/utils';

/**
 * Styled button with variants — used throughout modals, toolbars, and editor actions.
 *
 * Variants:
 * - "primary"  — Filled accent button (actions like Export, Save)
 * - "secondary" — Bordered ghost button (Cancel, Close, Open)
 * - "ghost"    — Minimal hover-only button (inline actions)
 *
 * @param {Object}    props
 * @param {ReactNode} props.children  — Button content
 * @param {Function}  [props.onClick]  — Click handler
 * @param {string}    [props.variant]  — "primary" | "secondary" | "ghost" (default: "secondary")
 * @param {string}    [props.size]     — "sm" | "md" (default: "md")
 * @param {boolean}   [props.disabled] — Disabled state
 * @param {string}    [props.className] — Additional classes
 * @param {string}    [props.href]     — If provided, renders as <a> instead of <button>
 * @param {string}    [props.download]  — Download filename (for <a> links)
 * @param {string}    [props.title]    — Tooltip text
 */
export function Button({
  children,
  onClick,
  variant = 'secondary',
  size = 'md',
  disabled = false,
  className = '',
  href,
  download,
  title,
}) {
  const sizeClasses = size === 'sm'
    ? 'h-7 px-3 text-xs'
    : 'h-9 px-4 text-sm';

  const variantClasses = {
    primary: 'bg-emerald-600 hover:bg-emerald-500 border-emerald-500/30 text-white font-semibold',
    secondary: 'bg-white/5 hover:bg-white/10 border-white/15 text-white/80 font-semibold',
    ghost: 'bg-transparent hover:bg-white/[0.06] border-transparent text-white/60 hover:text-white/80 font-medium',
  }[variant];

  const cls = cn(
    'rounded-full border transition-colors inline-flex items-center justify-center gap-1.5',
    sizeClasses,
    variantClasses,
    disabled && 'opacity-40 cursor-not-allowed pointer-events-none',
    className,
  );

  if (href) {
    return (
      <a href={href} target="_blank" rel="noreferrer" download={download} className={cls} title={title}>
        {children}
      </a>
    );
  }

  return (
    <button onClick={onClick} disabled={disabled} className={cls} title={title}>
      {children}
    </button>
  );
}
